import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import type { ManualListInput } from "@/api/router/manuals/schemas";
import { orpc } from "@/client";
import { usePageVisible } from "@/hooks/use-page-visible";
import { errorCode, notifyError } from "@/lib/error-code";
import { ManualLabels } from "@/lib/labels/manuals";
import { PollCadence } from "@/lib/poll-cadence";

function useInvalidate() {
	const queryClient = useQueryClient();

	return () => {
		void queryClient.invalidateQueries({ queryKey: orpc.manuals.key() });
		void queryClient.invalidateQueries({ queryKey: orpc.clinica.key() });
	};
}

function useInvalidateLists() {
	const queryClient = useQueryClient();

	return (id: number) => {
		void queryClient.invalidateQueries({ queryKey: orpc.manuals.list.key() });
		void queryClient.invalidateQueries({ queryKey: orpc.manuals.drafts.key() });
		void queryClient.invalidateQueries({ queryKey: orpc.manuals.get.key({ input: { id } }) });
	};
}

const onError = notifyError(ManualLabels.error);

export function useManualList(input: ManualListInput) {
	return useQuery(orpc.manuals.list.queryOptions({ input, placeholderData: keepPreviousData }));
}

export function useManualDrafts() {
	const visible = usePageVisible();

	return useQuery(
		orpc.manuals.drafts.queryOptions({
			refetchInterval: () => PollCadence.pipeline({ visible }),
		}),
	);
}

export function useManualEdit() {
	const invalidateLists = useInvalidateLists();

	return useMutation(
		orpc.manuals.edit.mutationOptions({
			onSuccess: (result) => {
				invalidateLists(result.id);
				toast.success(ManualLabels.saved);
			},
			onError,
		}),
	);
}

export function useManualDetail(id: number) {
	return useQuery(
		orpc.manuals.get.queryOptions({
			input: { id },
			retry: (_failureCount, error) => errorCode(error) !== "NOT_FOUND",
		}),
	);
}

export function useManualDeliver() {
	const invalidate = useInvalidate();

	return useMutation(orpc.manuals.deliver.mutationOptions({ onSuccess: invalidate, onError }));
}

export function useManualRevoke() {
	const invalidate = useInvalidate();

	return useMutation(orpc.manuals.revoke.mutationOptions({ onSuccess: invalidate, onError }));
}
